import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@sakhtar/design-system/react';
import { checkSlugAvailability } from '../../lib/publicApi.js';
import { ApiError } from '../../lib/apiClient.js';
import { AuthHeroPanel, AuthMobileHeader, useIsDesktopAuth } from '../auth/AuthHeroPanel.js';

// Same shape ADR-001 §2.2 step 2 validates server-side (`apps/api/src/tenant/resolve.ts`
// SLUG_SHAPE).
const SLUG_SHAPE = /^[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])?$/;

type SlugState = 'idle' | 'checking' | 'found' | 'notfound' | 'neterr';

/**
 * UI_REFERENCE_SOURCE: Sakhtar Login.dc.html, slug screen
 *   (`isSlugScreen`) — desktop (1440×900 split hero/form) and mobile
 *   (390×844 stacked) layouts read directly via DesignSync this session.
 * status: PARTIAL_REAL
 * reconciliation:
 *   - Live "does this org exist" check is real, not the prototype's fake
 *     1.1s timeout: `checkSlugAvailability` (already used by `/start/trial`
 *     and `LoginPage`'s own not-found detection). `available: true` means
 *     "free to register", i.e. no such org — the design's `slugMissing`
 *     state, inverted from the signup flow's own reading of the same field.
 *     A public slug lookup is not a tenant-enumeration oracle here: `/t/
 *     {slug}` is itself a public URL, not a secret (see `checkSlugAvailability`'s
 *     own doc comment in trial-signup.ts).
 *   - "بازیابی نشانی با شماره موبایل" (recover slug by mobile) has no real
 *     backend and is not rendered — AGENTS.md §1, no UI implying a
 *     capability that doesn't exist yet.
 */
export function LoginBySlugPage() {
  const navigate = useNavigate();
  const isDesktop = useIsDesktopAuth();
  const [slug, setSlug] = useState('');
  const [slugState, setSlugState] = useState<SlugState>('idle');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const redirectRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    return () => {
      clearTimeout(debounceRef.current);
      clearTimeout(redirectRef.current);
    };
  }, []);

  function runCheck(candidate: string) {
    clearTimeout(debounceRef.current);
    clearTimeout(redirectRef.current);
    if (!SLUG_SHAPE.test(candidate)) {
      setSlugState('idle');
      return;
    }
    setSlugState('checking');
    debounceRef.current = setTimeout(() => {
      checkSlugAvailability(candidate)
        .then((result) => {
          if (result.available) {
            setSlugState('notfound');
            return;
          }
          setSlugState('found');
          redirectRef.current = setTimeout(() => navigate(`/t/${candidate}/login`), 500);
        })
        .catch((err: unknown) => {
          if (err instanceof ApiError && err.code === 'RATE_LIMITED') {
            setSlugState('neterr');
            return;
          }
          setSlugState('neterr');
        });
    }, 400);
  }

  function onSlugChange(value: string) {
    const normalized = value.replace(/[^a-zA-Z0-9-]/g, '').toLowerCase();
    setSlug(normalized);
    runCheck(normalized.trim());
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    const trimmed = slug.trim();
    if (trimmed === '' || slugState === 'checking' || slugState === 'found') return;
    runCheck(trimmed);
  }

  const slugEmpty = slug.trim() === '';
  const busy = slugState === 'checking' || slugState === 'found';

  const field = (
    <div className="sk-field">
      <label>نشانی سازمان</label>
      <div className="sk-input-group" style={{ direction: 'ltr' }}>
        {isDesktop ? (
          <span style={{ color: 'var(--sk-fg-subtle)', display: 'flex' }}>
            <Icon name="globe" size="sm" />
          </span>
        ) : null}
        <span
          style={{
            fontSize: 13,
            color: 'var(--sk-fg-subtle)',
            whiteSpace: 'nowrap',
            paddingInlineStart: 2,
          }}
        >
          cloud.sakhtar.net/t/
        </span>
        <input
          value={slug}
          onChange={(event) => onSlugChange(event.target.value)}
          placeholder="kimia-tejarat"
          autoFocus
          style={{ direction: 'ltr', textAlign: 'left', fontWeight: 600 }}
        />
        {slugState === 'checking' ? (
          <span className="sk-spin" style={{ width: 15, height: 15, flex: '0 0 auto' }} />
        ) : null}
        {slugState === 'found' ? (
          <span style={{ color: 'var(--sk-success)', display: 'flex' }}>
            <Icon name="check" size="sm" />
          </span>
        ) : null}
        {slugState === 'notfound' ? (
          <span style={{ color: 'var(--sk-danger)', display: 'flex' }}>
            <Icon name="x" size="sm" />
          </span>
        ) : null}
      </div>
      {slugState === 'idle' && isDesktop ? (
        <span className="sk-hint">
          مثال: اگر نشانی شما{' '}
          <span style={{ direction: 'ltr', unicodeBidi: 'isolate', whiteSpace: 'nowrap' }}>
            cloud.sakhtar.net/t/kimia-tejarat
          </span>{' '}
          است، فقط{' '}
          <span style={{ direction: 'ltr', unicodeBidi: 'isolate', whiteSpace: 'nowrap' }}>
            kimia-tejarat
          </span>{' '}
          را وارد کنید.
        </span>
      ) : null}
      {slugState === 'checking' ? <span className="sk-hint">در حال بررسی نشانی…</span> : null}
      {slugState === 'found' ? (
        <span className="sk-hint" style={{ color: 'var(--sk-success)' }}>
          سازمان پیدا شد — در حال انتقال…
        </span>
      ) : null}
      {slugState === 'notfound' && !isDesktop ? (
        <span className="sk-hint" style={{ color: 'var(--sk-danger)' }}>
          چنین سازمانی پیدا نشد. املای نشانی را بررسی کنید.
        </span>
      ) : null}
    </div>
  );

  if (!isDesktop) {
    return (
      <div
        className="sk-app sk-auth-mobile"
        dir="rtl"
        style={{
          width: '100%',
          minHeight: '100dvh',
          background: 'var(--sk-bg-canvas)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 26,
          padding: '0 24px',
        }}
      >
        <AuthMobileHeader />
        <form
          noValidate
          onSubmit={submit}
          style={{ display: 'flex', flexDirection: 'column', gap: 18 }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <h1 style={{ margin: 0, fontSize: 19, fontWeight: 700, color: 'var(--sk-fg)' }}>
              ورود به فضای کاری سازمان
            </h1>
            <p style={{ margin: 0, fontSize: 13, lineHeight: 2, color: 'var(--sk-fg-muted)' }}>
              نشانی اختصاصی سازمان خود را وارد کنید.
            </p>
          </div>
          {field}
          <button
            type="submit"
            className="sk-btn sk-btn--primary sk-btn--lg"
            disabled={slugEmpty || busy}
            style={{ width: '100%' }}
          >
            ادامه
          </button>
        </form>
      </div>
    );
  }

  return (
    <div
      className="sk-app"
      dir="rtl"
      style={{ width: '100vw', height: '100dvh', background: 'var(--sk-bg-canvas)' }}
    >
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          direction: 'ltr',
        }}
      >
        <AuthHeroPanel />
        <div
          style={{
            direction: 'rtl',
            display: 'grid',
            placeContent: 'center',
            background: 'var(--sk-bg-canvas)',
            padding: '0 40px',
          }}
        >
          <div
            style={{
              width: 440,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 26,
            }}
          >
            <form
              noValidate
              onSubmit={submit}
              style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 22 }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--sk-fg)' }}>
                  ورود به فضای کاری سازمان
                </h1>
                <p style={{ margin: 0, fontSize: 13, lineHeight: 2, color: 'var(--sk-fg-muted)' }}>
                  نشانی اختصاصی سازمان خود را وارد کنید تا به صفحه ورود آن منتقل شوید.
                </p>
              </div>

              {field}

              {slugState === 'notfound' ? (
                <div
                  style={{
                    display: 'flex',
                    gap: 11,
                    alignItems: 'flex-start',
                    padding: '14px 16px',
                    borderRadius: 'var(--sk-r-md)',
                    background: 'var(--sk-danger-soft)',
                    border: '1px solid color-mix(in oklch, var(--sk-danger), transparent 78%)',
                  }}
                >
                  <span
                    style={{
                      color: 'var(--sk-danger)',
                      display: 'flex',
                      paddingTop: 2,
                      flex: '0 0 auto',
                    }}
                  >
                    <Icon name="warn" size="sm" />
                  </span>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--sk-fg)' }}>
                      چنین سازمانی پیدا نشد
                    </span>
                    <span style={{ fontSize: 12, lineHeight: 1.9, color: 'var(--sk-fg-muted)' }}>
                      نشانی واردشده در سامانه ثبت نشده است. املای آن را بررسی کنید یا با مدیر سیستم
                      سازمانتان تماس بگیرید.
                    </span>
                  </div>
                </div>
              ) : null}

              {slugState === 'neterr' ? (
                <div
                  style={{
                    display: 'flex',
                    gap: 11,
                    alignItems: 'flex-start',
                    padding: '14px 16px',
                    borderRadius: 'var(--sk-r-md)',
                    background: 'var(--sk-warn-soft)',
                    border: '1px solid color-mix(in oklch, var(--sk-warn), transparent 78%)',
                  }}
                >
                  <span
                    style={{ color: '#8a6412', display: 'flex', paddingTop: 2, flex: '0 0 auto' }}
                  >
                    <Icon name="warn" size="sm" />
                  </span>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--sk-fg)' }}>
                      ارتباط با سرور برقرار نشد
                    </span>
                    <span style={{ fontSize: 12, lineHeight: 1.9, color: 'var(--sk-fg-muted)' }}>
                      بررسی نشانی انجام نشد. چند لحظه بعد دوباره تلاش کنید.
                    </span>
                  </div>
                </div>
              ) : null}

              <button
                type="submit"
                className="sk-btn sk-btn--primary sk-btn--lg"
                disabled={slugEmpty || busy}
                style={{ width: '100%' }}
              >
                {busy ? (
                  <>
                    <span className="sk-spin" style={{ width: 16, height: 16 }} />
                    در حال بررسی…
                  </>
                ) : (
                  'ادامه'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
